/*
 * Header-to-field matching, requirement checks, mapping confirmation, and display ordering —
 * shared by the importer's column-matching and data-review steps. The engine maps file columns
 * by index and never reorders the data itself, so the ordering helper only changes render order.
 */

import _ from 'lodash';
import Fuse from 'fuse.js';

// Data
import { IImportColumn, ImportCell, ImportRow, ImportTemplate } from './importer.interface';
import { IColumnMap } from './ImportColumnMatcher/import-matcher.interface';
import { Sport, SportTerminology } from '../../../lib/types';
import { SPORT_TERMINOLOGY } from '../../../lib/constants';

// File-column indices ordered so columns mapped to a required field come first in
// `requiredColumns` declaration order; the rest keep file order.
export const orderColumnIndicesByRequiredFirst = (
  columnMap: IColumnMap[],
  requiredColumns: IImportColumn[],
  columnCount: number = columnMap.length
): number[] => {
  // Ranked by the mapped field's key, so a hand-mapped column ranks by the field it now points at.
  const requiredRank: { [key: string]: number } = Object.create(null);
  requiredColumns.forEach((column, rank) => { requiredRank[column.key] = rank; });

  // _.sortBy is stable, so unranked columns keep file order.
  return _.sortBy(_.range(columnCount), index => {
    const mapping = columnMap[index];
    // An ignored column is dropped at submission and never satisfies its field, so it ranks with
    // the unmapped ones rather than heading the list as the field it points at.
    const key = mapping?.isIgnored ? undefined : mapping?.column?.key;
    const rank = key === undefined ? undefined : requiredRank[key];

    return rank === undefined ? requiredColumns.length : rank;
  });
};

// The mapping's field definition, refreshed from the current expected columns — a consumer can
// redefine a field mid-flow, and a stored definition would keep the validators it was built with.
const refreshedMapping = (mapping: IColumnMap, expectedColumns: IImportColumn[]): IColumnMap => {
  // Keyed on key AND set: expectedColumns flattens the repeated sets in, so a repeated-set
  // column and a top-level one may share a key and only the pair identifies the definition.
  const current = mapping.column && _.find(expectedColumns,
    ({ key, set }) => key === mapping.column?.key && set === mapping.column?.set);

  if (!current || current === mapping.column) return mapping;

  // The operator's own format pick lives on the mapping, so it survives the swap.
  const format = mapping.column?.format ?? current.format;

  return { ...mapping, column: format === current.format ? current : { ...current, format } };
};

// Every mapping's definition brought up to date. Returns the same array when nothing changed, so
// an unstable `expectedColumns` identity can't drive a render loop.
export const refreshMappedColumns = (
  columnMap: IColumnMap[],
  expectedColumns: IImportColumn[]
): IColumnMap[] => {
  const next = columnMap.map(mapping => (mapping ? refreshedMapping(mapping, expectedColumns) : mapping));

  return next.some((mapping, index) => mapping !== columnMap[index]) ? next : columnMap;
};

// Locks every unconfirmed mapping: a mapped column confirms; an unmapped one has nothing to
// confirm, so it locks as ignored — mirroring the per-column Confirm/Ignore buttons.
export const confirmAllColumnMappings = (columnMap: IColumnMap[]): IColumnMap[] =>
  columnMap.map(mapping => mapping.isLocked
    ? mapping
    : { column: mapping.column, isIgnored: !mapping.column, isLocked: true });

// Locks every unconfirmed mapping as ignored, so a discarded column drops out of both the
// review table and the submitted documents instead of silently missing only from the submit.
export const discardUnconfirmedColumnMappings = (columnMap: IColumnMap[]): IColumnMap[] =>
  columnMap.map(mapping => mapping.isLocked
    ? mapping
    : { column: mapping.column, isIgnored: true, isLocked: true });

// Columns no confirmed file column points at. An ignored column is dropped at submission, so it
// never satisfies the field it maps to — required or not.
export const findUnmatchedColumns = (columns: IImportColumn[], columnMap: IColumnMap[]): IImportColumn[] =>
  columns.filter(column => !columnMap.some(
    mapItem => mapItem?.column?.key === column.key && mapItem.isLocked && !mapItem.isIgnored
  ));

// The fuzzy index the matching step pre-maps headers with, behind any matchPrefixes hit.
export const createColumnFuse = (columns: IImportColumn[]): Fuse<IImportColumn> =>
  new Fuse(columns, { ignoreLocation: true, includeScore: true, threshold: 0.3, keys: ['label', 'matchAliases', 'matchPrefixes'] });

const IMPORT_TEMPLATE_TERMS: Record<ImportTemplate, SportTerminology> = {
  player: SportTerminology.ATHLETE,
  'assessment-session': SportTerminology.SESSION,
};

// One header-only template per sport and importer, named in that sport's terms (hockey/ice-time-…).
export const importTemplateUrl = (template: ImportTemplate, sport: Sport): string =>
  `${process.env.PUBLIC_URL}/templates/${sport}/${_.kebabCase(SPORT_TERMINOLOGY[sport][IMPORT_TEMPLATE_TERMS[template]])}-import-template.csv`;

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

// Field a file header maps to. A matchPrefixes hit wins over the fuzzy index and the longest
// prefix wins among hits, so one prefix covers every example suffix a header might carry.
export const matchColumnByHeader = (
  header: string,
  columns: IImportColumn[],
  fuse: Fuse<IImportColumn>
): IImportColumn | undefined => {
  const normalizedHeader = normalizeHeader(header);
  let match: { column: IImportColumn; length: number } | undefined;

  columns.forEach(column => {
    column.matchPrefixes?.forEach(prefix => {
      const normalizedPrefix = normalizeHeader(prefix);
      const isLonger = !match || normalizedPrefix.length > match.length;

      if (isLonger && normalizedHeader.startsWith(normalizedPrefix)) {
        match = { column, length: normalizedPrefix.length };
      }
    });
  });

  return match ? match.column : fuse.search(normalizedHeader)[0]?.item;
};

// Pads every row shorter than the header row with blank cells, so a trimmed export's rows stay
// aligned to the header's column indices. Rows at or past the header width are left untouched.
export const padRowsToHeaderWidth = (rows: ImportRow[], headerRowIndex: number | null): ImportRow[] => {
  const headerRow = headerRowIndex === null ? undefined : rows[headerRowIndex];
  // The header row's own length, so every index the matcher builds a column for has a cell.
  const headerWidth = headerRow ? headerRow.length : 0;

  if (!headerWidth) return rows;

  return rows.map(row => row.length >= headerWidth
    ? row
    : row.concat(new Array<ImportCell>(headerWidth - row.length).fill('')));
};

// File-column indices whose every data cell is blank. Header text is not content: a titled column
// with no values still imports nothing, so the matcher drops it rather than offering it to map.
export const findEmptyColumnIndices = (
  rows: ImportRow[],
  headerRowIndex: number | null,
  columnCount: number
): number[] => {
  const dataRows = rows.filter((row, index) => index !== headerRowIndex);

  // A header-only file has nothing to judge emptiness by, so every column stays mappable.
  if (!dataRows.length) return [];

  return _.range(columnCount).filter(index => dataRows.every(row => !row[index]?.trim()));
};

// Display-order index of the next column still awaiting a Confirm/Ignore decision after
// `afterIndex`, wrapping past the end so skipped columns above are reached before the list runs out.
export const findNextUnconfirmedColumnIndex = (
  columnOrder: number[],
  columnMap: IColumnMap[],
  afterIndex: number
): number | null => {
  const position = columnOrder.indexOf(afterIndex);
  // Rotating past `afterIndex` drops it from the search, so its own pending state never matches.
  const searchOrder = position === -1
    ? columnOrder
    : [...columnOrder.slice(position + 1), ...columnOrder.slice(0, position)];

  // An overflow column has no columnMap entry and renders no card, so it is not a scroll target.
  const next = searchOrder.find(index => columnMap[index] && !columnMap[index].isLocked);

  return next === undefined ? null : next;
};
